import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Store, Plus, Package, FolderTree, Eye, ExternalLink } from 'lucide-react';

const DashboardHome = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalProducts: 0, totalCategories: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const shopsRes = await api.get('/shops');
      setShops(shopsRes.data);

      // Calculate stats
      let totalProducts = 0;
      let totalCategories = 0;
      
      for (const shop of shopsRes.data) {
        try {
          const [catsRes, prodsRes] = await Promise.all([
            api.get(`/shops/${shop.id}/categories`),
            api.get(`/shops/${shop.id}/products`)
          ]);
          totalCategories += catsRes.data.length;
          totalProducts += prodsRes.data.length;
        } catch (e) {
          console.error('Error fetching shop data:', e);
        }
      }
      
      setStats({ totalProducts, totalCategories });
    } catch (error) {
      console.error('Error fetching shops:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">
          Bienvenue, {user?.name} !
        </h1>
        <p className="text-muted-foreground mt-1">
          Gérez vos catalogues digitaux
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Boutiques</CardTitle>
            <Store className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{shops.length}</div>
            <p className="text-xs text-muted-foreground">
              {user?.plan === 'STARTER' ? 'Max 1 avec Starter' : 'Illimité avec votre plan'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Catégories</CardTitle>
            <FolderTree className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCategories}</div>
            <p className="text-xs text-muted-foreground">
              {user?.plan === 'STARTER' ? 'Max 3 par boutique' : 'Illimitées'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Produits</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalProducts}</div>
            <p className="text-xs text-muted-foreground">
              {user?.plan === 'STARTER' ? 'Max 30 par boutique' : 'Illimités'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      <Card>
        <CardHeader>
          <CardTitle>Actions rapides</CardTitle>
          <CardDescription>Gérez vos boutiques et catalogues</CardDescription>
        </CardHeader>
        <CardContent>
          {shops.length === 0 ? (
            <div className="text-center py-8">
              <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Aucune boutique</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Créez votre première boutique pour commencer
              </p>
              <Button onClick={() => navigate('/app/shops')} data-testid="create-first-shop">
                <Plus className="w-4 h-4 mr-2" />
                Créer ma boutique
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {shops.slice(0, 3).map((shop) => (
                <div 
                  key={shop.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {shop.logoUrl ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${shop.logoUrl}`}
                        alt={shop.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Store className="w-6 h-6 text-primary" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold">{shop.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {shop.isPublished ? (
                          <span className="text-green-600 dark:text-green-400">Publié</span>
                        ) : (
                          <span className="text-yellow-600 dark:text-yellow-400">Brouillon</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {shop.isPublished && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(`/s/${shop.slug}`, '_blank')}
                        title="Voir le catalogue"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/app/shops/${shop.id}/products`)}
                    >
                      Gérer
                    </Button>
                  </div>
                </div>
              ))}
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/app/shops')}
              >
                Voir toutes les boutiques
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan info */}
      <Card className={user?.plan === 'STARTER' ? 'border-primary/50' : ''}>
        <CardHeader>
          <CardTitle>Votre plan : {user?.plan}</CardTitle>
          <CardDescription>
            {user?.plan === 'STARTER' 
              ? 'Passez au plan PRO pour débloquer toutes les fonctionnalités'
              : user?.plan === 'PRO'
              ? 'Passez au PREMIUM pour les fonctionnalités IA'
              : 'Vous bénéficiez de toutes les fonctionnalités'
            }
          </CardDescription>
        </CardHeader>
        {user?.plan !== 'PREMIUM' && (
          <CardContent>
            <Button onClick={() => navigate('/app/billing')} data-testid="upgrade-plan">
              Voir les plans
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default DashboardHome;
